import {
  ConsoleOutputLevel,
  DepID_ClientDownloadSessionConfiguration,
  DepID_ClientDownloadSessionConfigurationMessage,
  DepID_ClientInitialApplicationEventsBeacon,
  DepID_ClientInitialSessionConfigSourceReceived,
  DepID_ClientScheduleApplicationEventsBeaconInterval,
  DepID_ClientScheduleMeasurementSchedulerRunInterval,
  DepID_ClientScheduleSessionConfigurationRefreshInterval,
  DepID_ClientSubsequentSessionConfigSourceReceived,
  HasValue,
  OutputToConsoleFunc,
  SessionMetadata,
} from "../@types"
import {
  ApplicationEvents,
  ClientInitialized,
  geoHeaderValues,
} from "./applicationEvents"
import { Config3Source, createConfig3 } from "./config3"
import { createIntervalManager } from "./intervalManagement"
import { Scheduler } from "./measurementScheduler"
import { init } from "./openinsights/init"
import { PerformanceEntryManager } from "./openinsights/resourceTiming"
import { PageSettings } from "./pageSettings"
import { PlatformTestObject } from "./platformTestObject"
import {
  buildPreliminaryPopulation,
  initializeScheduledPopulation,
  updateScheduledPopulation,
} from "./platformTestObjects"
import { ResettableEventCounter } from "./resettableEventCounter"
import { RuntimeValues } from "./runtimeValues"
import sampleDNSClientInfo from "./sampleDNSClientInfo"
import { performSentinelDetection } from "./sentinelDetection"
import { SessionConfig } from "./sessionConfig"

export interface Dependencies {
  addDocumentReadyStateChangeListener: (callback: () => void) => void
  applicationEvents: ApplicationEvents
  /**
   * @param timeout Timeout in milliseconds
   */
  callAbortSignalTimeout: (depID: number, timeout: number) => AbortSignal
  callFetch: (url: string, options?: RequestInit) => Promise<Response>
  callSendBeacon: (depID: number, url: string) => boolean
  callSetInterval: (depID: number, fn: () => void, delay: number) => number
  callSetTimeout: (depID: number, fn: () => void, timeout: number) => number
  callClearInterval: (depID: number, intervalID: number) => void
  callClearTimeout: (depID: number, timeoutID: number) => void
  callClearResourceTimings: () => void
  getDocumentReadyState: () => DocumentReadyState
  maxTargetFrequency: HasValue<number>
  measurementScheduler: Scheduler
  newAbortController: () => AbortController
  newDate: (depID: number) => Date
  newEpochTimestamp: (depID: number) => number
  newMonotonicTimestamp: (depID: number) => number
  newRandomNumber: (depID: number) => number
  newUUIDv4: () => string
  outputToConsole: OutputToConsoleFunc
  performanceEntryAwaitTimeouts: ResettableEventCounter
  performanceEntryManager: PerformanceEntryManager
  runtimeValues: RuntimeValues
}

export type ClientInitializationInfo = {
  sessionMetadata: SessionMetadata
  pageSettings: PageSettings
  sessionConfigURL: string
  clientInitializationEvent: ClientInitialized
}

const newAppEventLoggingEndpointMessage = (
  appEventLoggingEndpoint: string | null,
) => `Application event logging endpoint: ${appEventLoggingEndpoint}`

/**
 * Runs sentinel detection with consistent error handling and state management
 */
async function runSentinelDetection(
  dependencies: Dependencies,
  sessionMetadata: SessionMetadata,
  population: PlatformTestObject[],
  sessionConfig: SessionConfig,
  context: string,
): Promise<PlatformTestObject[]> {
  try {
    const detectionResult = await performSentinelDetection(
      {
        applicationEvents: dependencies.applicationEvents,
        callClearTimeout: dependencies.callClearTimeout,
        callFetch: dependencies.callFetch,
        callSendBeacon: dependencies.callSendBeacon,
        callSetTimeout: dependencies.callSetTimeout,
        callClearResourceTimings: dependencies.callClearResourceTimings,
        maxTargetFrequency: dependencies.maxTargetFrequency,
        measurementScheduler: dependencies.measurementScheduler,
        newAbortController: dependencies.newAbortController,
        newDate: dependencies.newDate,
        newEpochTimestamp: dependencies.newEpochTimestamp,
        newMonotonicTimestamp: dependencies.newMonotonicTimestamp,
        newRandomNumber: dependencies.newRandomNumber,
        outputToConsole: dependencies.outputToConsole,
        performanceEntryAwaitTimeouts:
          dependencies.performanceEntryAwaitTimeouts,
        performanceEntryManager: dependencies.performanceEntryManager,
        runtimeValues: dependencies.runtimeValues,
      },
      sessionMetadata,
      population,
      sessionConfig,
    )

    return detectionResult.sentinelPlatforms
  } catch (error) {
    // Log error with more context; use error.name for classification to avoid
    // fragile message parsing that can vary between browsers and implementations
    let errorType = "Unknown error"
    if (error instanceof Error) {
      errorType = error.name || "Error"
    }

    dependencies.outputToConsole(
      ConsoleOutputLevel.warn,
      () =>
        `${context} sentinel detection failed (${errorType}):` +
        ` ${error instanceof Error ? error.message : String(error)}. ` +
        "Proceeding with no sentinels;" +
        " this may impact measurement accuracy or session behavior.",
    )

    return []
  }
}

// Main public API
export const run = (
  dependencies: Dependencies,
  clientInitInfo: ClientInitializationInfo,
) => {
  // Create interval manager with focused dependencies
  const intervalManager = createIntervalManager({
    callSetInterval: dependencies.callSetInterval,
    callSetTimeout: dependencies.callSetTimeout,
    callClearInterval: dependencies.callClearInterval,
    outputToConsole: dependencies.outputToConsole,
    measurementScheduler: dependencies.measurementScheduler,
    triggerSessionRefresh: (clientInfo) => {
      downloadSessionConfiguration(
        dependencies,
        clientInfo,
        newSubsequentSessionConfigSourceReceivedFunc(dependencies, clientInfo),
      ).then(newProcessNewSessionConfigurationFunc(dependencies, clientInfo))
    },
    triggerBeacon: () => dependencies.applicationEvents.beacon(),
  })

  // Start engines
  const pageSettings = clientInitInfo.pageSettings
  downloadSessionConfiguration(
    dependencies,
    clientInitInfo,
    onInitialSessionConfigSourceReceived,
  ).then((sessionConfigInstance) => {
    if (sessionConfigInstance) {
      const runtimeValues = dependencies.runtimeValues

      // Delegate scheduling to IntervalManager
      intervalManager.scheduleSessionRefresh(
        runtimeValues,
        sessionConfigInstance,
        clientInitInfo,
      )
      intervalManager.scheduleBeaconInterval(
        runtimeValues,
        sessionConfigInstance,
      )
      intervalManager.scheduleSchedulerRuns(
        runtimeValues,
        sessionConfigInstance,
      )
    }
  })

  function onInitialSessionConfigSourceReceived(
    asn: number,
    continentCode: string,
    countryCode: string,
    source: Config3Source,
  ) {
    const newSessionConfig = createConfig3(source, pageSettings)
    dependencies.runtimeValues.sessionConfigLastUpdatedAt =
      dependencies.newDate(DepID_ClientInitialSessionConfigSourceReceived)
    dependencies.applicationEvents.applicationEventLoggingEndpointBase =
      newSessionConfig.beaconEndpointInfo.applicationEventsBase
    dependencies.outputToConsole(ConsoleOutputLevel.debug, () =>
      newAppEventLoggingEndpointMessage(
        dependencies.applicationEvents.applicationEventLoggingEndpointBase,
      ),
    )
    dependencies.runtimeValues.disallowedNetworks =
      newSessionConfig.siteOwnerSettings.networksDisallowed
    dependencies.runtimeValues.updateMeasurementsEnabledForNetwork(asn)

    // Skip the opening session unless measurements are enabled
    if (dependencies.runtimeValues.measurementsEnabled) {
      const startDelay =
        typeof pageSettings.measurementStartDelay === "number"
          ? pageSettings.measurementStartDelay
          : newSessionConfig.siteOwnerSettings.measurementStartDelay

      // Schedule the opening session
      dependencies.callSetTimeout(
        DepID_ClientInitialSessionConfigSourceReceived,
        () => {
          init(
            dependencies.getDocumentReadyState,
            dependencies.addDocumentReadyStateChangeListener,
            0,
            [
              {
                fetchSessionConfiguration: () =>
                  Promise.resolve(newSessionConfig),
                produceExecutables: async (sessionConfigInstance: unknown) =>
                  produceExecutables(sessionConfigInstance, newSessionConfig),
              },
            ],
            dependencies.outputToConsole,
          ).then(() =>
            // Insert a slight delay in order to let the last beacon
            // complete. In most cases this avoids sending another
            // application event beacon on the interval.
            dependencies.callSetTimeout(
              DepID_ClientInitialApplicationEventsBeacon,
              () => dependencies.applicationEvents.beacon(),
              1000,
            ),
          )
        },
        startDelay * 1000,
      )
    } else {
      dependencies.outputToConsole(
        ConsoleOutputLevel.info,
        () => "Doppler measurements disabled (initial session config)",
      )
    }
    return newSessionConfig

    async function produceExecutables(
      sessionConfigInstance: unknown,
      sessionConfig: SessionConfig,
    ) {
      dependencies.outputToConsole(
        ConsoleOutputLevel.debug,
        () => "Starting sentinel detection (initial session config)",
      )
      // Perform sentinel detection first
      const sentinelsUsed = await runSentinelDetection(
        dependencies,
        clientInitInfo.sessionMetadata,
        buildPreliminaryPopulation(
          source.o,
          sessionConfig,
          dependencies.outputToConsole,
        ),
        sessionConfig,
        "Initial",
      )
      dependencies.outputToConsole(ConsoleOutputLevel.debug, () => {
        const sentinelIds = sentinelsUsed.map(
          (platform) => platform.dopplerPlatformID,
        )
        return sentinelIds.length > 0
          ? `Sentinel detection completed (initial session config): ${sentinelIds.join(", ")}`
          : "Sentinel detection completed (initial session config): no sentinels used"
      })

      // Check dormancy state after sentinel detection
      if (dependencies.runtimeValues.trackerBlockingDetected) {
        dependencies.outputToConsole(
          ConsoleOutputLevel.info,
          () =>
            "Skipping regular measurement initialization due to " +
            "tracker blocking detection (dormancy mode)",
        )
        // Return empty array - no measurements should be scheduled when dormant
        return []
      }

      // Only initialize regular measurements when NOT in dormancy mode
      return initializeScheduledPopulation(
        {
          applicationEvents: dependencies.applicationEvents,
          callClearTimeout: dependencies.callClearTimeout,
          callSendBeacon: dependencies.callSendBeacon,
          callSetTimeout: dependencies.callSetTimeout,
          callClearResourceTimings: dependencies.callClearResourceTimings,
          callFetch: dependencies.callFetch,
          maxTargetFrequency: dependencies.maxTargetFrequency,
          measurementScheduler: dependencies.measurementScheduler,
          newAbortController: dependencies.newAbortController,
          newDate: dependencies.newDate,
          newEpochTimestamp: dependencies.newEpochTimestamp,
          newMonotonicTimestamp: dependencies.newMonotonicTimestamp,
          newRandomNumber: dependencies.newRandomNumber,
          outputToConsole: dependencies.outputToConsole,
          performanceEntryAwaitTimeouts:
            dependencies.performanceEntryAwaitTimeouts,
          performanceEntryManager: dependencies.performanceEntryManager,
          runtimeValues: dependencies.runtimeValues,
        },
        clientInitInfo.sessionMetadata,
        pageSettings,
        /* eslint-disable @typescript-eslint/dot-notation */
        source["o"],
        /* eslint-enable @typescript-eslint/dot-notation */
        sessionConfigInstance as SessionConfig,
        sentinelsUsed,
      )
    }
  }
}

const newDownloadingSessionConfigurationMessage = (
  newDate: (depID: number) => Date,
) =>
  `Downloading new session configuration (${newDate(
    DepID_ClientDownloadSessionConfigurationMessage,
  )})`

/**
 * Downloads and processes session configuration data.
 *
 * Performs the HTTP request, JSON parsing, DNS client info sampling, and
 * geographic metadata extraction. As part of that work, also records
 * application events and updates request/session metadata in `runtimeValues`,
 * including `sessionStartTimestamp`, `sessionUUID`, `resolverIP`, and the
 * `lastReadSessConfHeader*` fields.
 *
 * Applying the parsed session configuration itself is delegated to the
 * provided handler, which owns any handler-specific runtime state updates
 * such as `sessionConfigLastUpdatedAt`.
 *
 * @param dependencies - Injected dependencies for network calls and logging
 * @param clientInitInfo - Client initialization information including config URL
 * @param handleSessionConfigSourceReceived - Handler responsible for processing
 *   the parsed config and updating delegated runtime state
 *   (e.g., `sessionConfigLastUpdatedAt`)
 * @returns Promise resolving to the result returned by the handler after the
 *   download/parsing side effects and delegated config processing complete
 */
export async function downloadSessionConfiguration(
  dependencies: Dependencies,
  clientInitInfo: ClientInitializationInfo,
  handleSessionConfigSourceReceived: (
    asn: number,
    continentCode: string,
    countryCode: string,
    sessionConfigSource: Config3Source,
  ) => SessionConfig | Promise<SessionConfig>,
) {
  dependencies.outputToConsole(ConsoleOutputLevel.info, () =>
    newDownloadingSessionConfigurationMessage(dependencies.newDate),
  )
  const sessionConfigMonotonicStartTime = dependencies.newMonotonicTimestamp(
    DepID_ClientDownloadSessionConfiguration,
  )
  const beforeDownloadEvent =
    dependencies.applicationEvents.recordBeforeSessionConfigDownloadEvent(
      dependencies.newEpochTimestamp(DepID_ClientDownloadSessionConfiguration),
      dependencies.runtimeValues,
    )
  dependencies.runtimeValues.sessionStartTimestamp = dependencies.newDate(
    DepID_ClientDownloadSessionConfiguration,
  )
  dependencies.runtimeValues.sessionUUID = dependencies.newUUIDv4()
  dependencies.runtimeValues.resolverIP = undefined

  // Also need to implement completed and error event reporting
  return dependencies
    .callFetch(clientInitInfo.sessionConfigURL)
    .then((response) => response.json() as Promise<Config3Source>)
    .then((source) =>
      sampleDNSClientInfo(dependencies, source, clientInitInfo.pageSettings),
    )
    .then(async (source) => {
      // Freshen session config metadata
      /* eslint-disable @typescript-eslint/dot-notation */
      const asn = source["g"]["a"]
      const continentCode = source["g"]["con"]
      const countryCode = source["g"]["c"]
      const stateCode = source["g"]["s"]
      /* eslint-enable @typescript-eslint/dot-notation */
      dependencies.runtimeValues.lastReadSessConfHeaderContinent = continentCode
      dependencies.runtimeValues.lastReadSessConfHeaderCountry = countryCode
      dependencies.runtimeValues.lastReadSessConfHeaderState = stateCode
      dependencies.runtimeValues.lastReadSessConfHeaderAsn = asn.toString(10)
      dependencies.applicationEvents.recordAfterSessionConfigDownloadEvent(
        dependencies.newEpochTimestamp(
          DepID_ClientDownloadSessionConfiguration,
        ),
        dependencies.runtimeValues,
        Math.floor(
          dependencies.newMonotonicTimestamp(
            DepID_ClientDownloadSessionConfiguration,
          ) - sessionConfigMonotonicStartTime,
        ),
      )
      // Update geo for the early application events that wouldn't
      // have it yet.
      const geoHeaders = geoHeaderValues(dependencies.runtimeValues)
      beforeDownloadEvent.splice(1, 4, ...geoHeaders)
      clientInitInfo.clientInitializationEvent.splice(1, 4, ...geoHeaders)

      // Handle both sync and async session config handlers
      return await Promise.resolve(
        handleSessionConfigSourceReceived(
          asn,
          continentCode,
          countryCode,
          source,
        ),
      )
    })
    .catch((e) => {
      // Prevent red stuff in the browser related to session config,
      // but anything here is probably worth immediate application
      // event logging
      if (e instanceof Error) {
        dependencies.applicationEvents.recordSessionConfigDownloadErrorEvent(
          dependencies.newEpochTimestamp(
            DepID_ClientDownloadSessionConfiguration,
          ),
          dependencies.runtimeValues,
          e.message,
        )
      }
      dependencies.applicationEvents.beacon()
    })
    .finally(() => {
      dependencies.outputToConsole(
        ConsoleOutputLevel.debug,
        () =>
          `Session config refresh completed at ${dependencies.newDate(
            DepID_ClientDownloadSessionConfiguration,
          )}`,
      )
    })
}

function newSubsequentSessionConfigSourceReceivedFunc(
  dependencies: Dependencies,
  clientInitInfo: ClientInitializationInfo,
) {
  return async (
    asn: number,
    continentCode: string,
    countryCode: string,
    source: Config3Source,
  ) => {
    const newSessionConfig = createConfig3(source, clientInitInfo.pageSettings)
    dependencies.runtimeValues.sessionConfigLastUpdatedAt =
      dependencies.newDate(DepID_ClientSubsequentSessionConfigSourceReceived)
    dependencies.runtimeValues.disallowedNetworks =
      newSessionConfig.siteOwnerSettings.networksDisallowed
    dependencies.runtimeValues.updateMeasurementsEnabledForNetwork(asn)
    if (!dependencies.runtimeValues.measurementsEnabled) {
      dependencies.outputToConsole(
        ConsoleOutputLevel.info,
        () => "Doppler measurements disabled (subsequent session config)",
      )
    }

    // Re-run sentinel detection on config updates to detect status changes
    await runSentinelDetection(
      dependencies,
      clientInitInfo.sessionMetadata,
      buildPreliminaryPopulation(
        source.o,
        newSessionConfig,
        dependencies.outputToConsole,
      ),
      newSessionConfig,
      "Subsequent",
    )

    updateScheduledPopulation(
      dependencies.measurementScheduler,
      clientInitInfo.sessionMetadata,
      /* eslint-disable @typescript-eslint/dot-notation */
      source["o"],
      /* eslint-enable @typescript-eslint/dot-notation */
      newSessionConfig.beaconEndpointInfo.dopplerEndpoints
        .dopplerResourceTimingEndpoint,
      newSessionConfig.beaconEndpointInfo.pulsarEndpointBase,
      dependencies.outputToConsole,
    )
    return newSessionConfig
  }
}

const newApplicationEventLoggingEndpointChangedMessage = (
  from: string | null,
  to: string | null,
) => `Application event logging endpoint changed from ${from} to ${to}`

const newApplicationEventLoggingIntervalChangedMessage = (
  from: number | undefined,
  to: number,
) =>
  `Application events beacon interval changed from ${from} to ${to} milliseconds`

const newSessionRefreshIntervalChangedMessage = (
  from: number | undefined,
  to: number,
) => `Session refresh interval changed from ${from} to ${to} milliseconds`

const newSchedulerRunIntervalChangedMessage = (
  from: number | undefined,
  to: number,
) => `Scheduler run interval changed from ${from} to ${to} milliseconds`

function newProcessNewSessionConfigurationFunc(
  dependencies: Dependencies,
  clientInitInfo: ClientInitializationInfo,
) {
  // Create interval manager for config change handling
  const intervalManager = createIntervalManager({
    callSetInterval: dependencies.callSetInterval,
    callSetTimeout: dependencies.callSetTimeout,
    callClearInterval: dependencies.callClearInterval,
    outputToConsole: dependencies.outputToConsole,
    measurementScheduler: dependencies.measurementScheduler,
    triggerSessionRefresh: (clientInfo) => {
      downloadSessionConfiguration(
        dependencies,
        clientInfo,
        newSubsequentSessionConfigSourceReceivedFunc(dependencies, clientInfo),
      ).then(newProcessNewSessionConfigurationFunc(dependencies, clientInfo))
    },
    triggerBeacon: () => dependencies.applicationEvents.beacon(),
  })

  return (newSessionConfigInstance: SessionConfig | void) => {
    if (newSessionConfigInstance) {
      const runtimeValues = dependencies.runtimeValues

      // Application event logging endpoint (unchanged)
      if (
        newSessionConfigInstance.beaconEndpointInfo.applicationEventsBase !==
        dependencies.applicationEvents.applicationEventLoggingEndpointBase
      ) {
        dependencies.outputToConsole(ConsoleOutputLevel.info, () =>
          newApplicationEventLoggingEndpointChangedMessage(
            dependencies.applicationEvents.applicationEventLoggingEndpointBase,
            newSessionConfigInstance.beaconEndpointInfo.applicationEventsBase,
          ),
        )
        dependencies.applicationEvents.applicationEventLoggingEndpointBase =
          newSessionConfigInstance.beaconEndpointInfo.applicationEventsBase
      }

      // Application events beacon interval - use IntervalManager
      if (
        newSessionConfigInstance.siteOwnerSettings
          .applicationEventsBeaconIntervalInMilliseconds !==
        runtimeValues.applicationEventsBeaconIntervalInMilliseconds
      ) {
        dependencies.outputToConsole(ConsoleOutputLevel.debug, () =>
          newApplicationEventLoggingIntervalChangedMessage(
            runtimeValues.applicationEventsBeaconIntervalInMilliseconds,
            newSessionConfigInstance.siteOwnerSettings
              .applicationEventsBeaconIntervalInMilliseconds,
          ),
        )
        const intervalID = runtimeValues.applicationEventsBeaconIntervalID
        if (typeof intervalID === "number") {
          dependencies.callClearInterval(
            DepID_ClientScheduleApplicationEventsBeaconInterval,
            intervalID,
          )
        }
        intervalManager.scheduleBeaconInterval(
          runtimeValues,
          newSessionConfigInstance,
        )
      }

      // Session refresh interval - use IntervalManager
      if (
        newSessionConfigInstance.siteOwnerSettings
          .sessionRefreshIntervalInMilliseconds !==
        runtimeValues.sessionRefreshDelayInMilliseconds
      ) {
        dependencies.outputToConsole(ConsoleOutputLevel.debug, () =>
          newSessionRefreshIntervalChangedMessage(
            runtimeValues.sessionRefreshDelayInMilliseconds,
            newSessionConfigInstance.siteOwnerSettings
              .sessionRefreshIntervalInMilliseconds,
          ),
        )
        const intervalID = runtimeValues.sessionConfigRefreshIntervalID
        if (typeof intervalID === "number") {
          dependencies.callClearInterval(
            DepID_ClientScheduleSessionConfigurationRefreshInterval,
            intervalID,
          )
        }
        intervalManager.scheduleSessionRefresh(
          runtimeValues,
          newSessionConfigInstance,
          clientInitInfo,
        )
      }

      // Scheduler run interval - use IntervalManager
      if (
        newSessionConfigInstance.siteOwnerSettings
          .schedulerRunIntervalInMilliseconds !==
        runtimeValues.schedulerRunIntervalInMilliseconds
      ) {
        dependencies.outputToConsole(ConsoleOutputLevel.debug, () =>
          newSchedulerRunIntervalChangedMessage(
            runtimeValues.schedulerRunIntervalInMilliseconds,
            newSessionConfigInstance.siteOwnerSettings
              .schedulerRunIntervalInMilliseconds,
          ),
        )
        const intervalID = runtimeValues.schedulerRunIntervalID
        if (typeof intervalID === "number") {
          dependencies.callClearInterval(
            DepID_ClientScheduleMeasurementSchedulerRunInterval,
            intervalID,
          )
        }
        intervalManager.scheduleSchedulerRuns(
          runtimeValues,
          newSessionConfigInstance,
        )
      }
    }
  }
}
